import { GridHookForm } from 'vnetwork-ui'
import { updateCustomerStatus } from '@/modules/Customer/services'
import { ModalProps } from '@/types/shared'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Alert, Dialog } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

export interface ChangeStatusProps extends ModalProps {
    customer_id: number
    current_status?: string
}

interface ChangeStatusFormValues {
    customer_status: string
    customer_note: string
}

/**
 * Valid status transitions from qualified-prospect:
 * - cool-lead: Khách tiềm năng thấp
 * - dead-lead: Khách không còn tiềm năng
 * - active: Khách hàng đang sử dụng dịch vụ
 * - trial: Khách đang dùng thử
 * - stopped: Khách đã dừng dịch vụ
 */
const CUSTOMER_STATUS_OPTIONS = (t: (key: string) => string) => [
    { value: 'cool-lead', label: t('customer:status.cool_lead') },
    { value: 'dead-lead', label: t('customer:status.dead_lead') },
    { value: 'active', label: t('customer:status.active') },
    { value: 'trial', label: t('customer:status.trial') },
    { value: 'stopped', label: t('customer:status.stopped') },
]

const ChangeStatus: React.FC<ChangeStatusProps> = ({
    onClose,
    open,
    customer_id,
}) => {
    const { t } = useTranslation()
    const formControl = useForm<ChangeStatusFormValues>({
        defaultValues: {
            customer_status: 'cool-lead',
            customer_note: '',
        },
        resolver: yupResolver(
            yup.object().shape({
                customer_status: yup.string().required(t('required')),
                customer_note: yup.string().when('customer_status', {
                    is: (val: string) =>
                        val === 'cool-lead' || val === 'dead-lead',
                    then: (schema) =>
                        schema.required(t('required-on-cancel-follow')),
                    otherwise: (schema) => schema,
                }),
            })
        ),
    })

    const selectedStatus = formControl.watch('customer_status')

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: updateCustomerStatus,
        },
        {
            reactQueryKeys: QUERY_KEYS.CUSTOMERS,
            successMessage: t('success-change-status'),
            onSuccess: () => {
                onClose()
            },
        }
    )

    const handleChangeStatus = (values: ChangeStatusFormValues) => {
        mutateAsync({
            customer_id,
            customer_status: values.customer_status,
            customer_note: values.customer_note || undefined,
        })
    }

    return (
        <Dialog title={t('change-status')} onClose={onClose} open={open}>
            <Alert variant="info">{t('change-status-description')}</Alert>
            <GridHookForm
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: t('save'),
                        type: 'submit',
                        variant: 'contained',
                        disabled: isPending,
                        loading: isPending,
                    },
                ]}
                data={[
                    {
                        className: 'md:col-span-4 col-span-1',
                        formItemProps: {
                            name: 'customer_status',
                            type: 'select',
                            label: t('new-status'),
                            placeholder: t('select-status'),
                            options: CUSTOMER_STATUS_OPTIONS(t),
                        },
                    },
                    {
                        className: 'md:col-span-4 col-span-1',
                        formItemProps: {
                            name: 'customer_note',
                            type: 'textarea',
                            placeholder: t('note'),
                            label:
                                selectedStatus === 'cool-lead' ||
                                selectedStatus === 'dead-lead'
                                    ? t('note-reason-change-status') + ' *'
                                    : t('note-reason-change-status'),
                        },
                    },
                ]}
                onSubmit={handleChangeStatus}
                className="mt-4"
            />
        </Dialog>
    )
}

export default ChangeStatus
